import { useReduxState } from 'hooks';
import { Button, RichTextValue, SortableList } from 'components/common';
import { FormGroup } from 'components/common/form';
import { Icon } from 'components/common/icons';
import { TaskPointWeightSelector } from 'components/common/tasks';
import { EditorBlockTranslationReference } from 'components/editor';
import {
  defaultBlockData,
  EditorBlockIsMandatorySettings,
  EditorBlockMoreSettings,
  EditorBlockRequireAnswerToProceedInfo
} from 'components/editor/blocks';
import { TextEditor } from 'components/editor/inputs';
import { EditorBlockSequencingOption, EditorBlockSequencingOrder } from '.';

import type { BlockSequencing } from 'shared-types';
import type {
  BlockProps,
  EditorStateProps
} from 'components/editor/blocks/EditorBlock';

export type BlockSequencingStateProps = Pick<
  EditorStateProps,
  'editorLanguageActive' | 'editorLanguageReference'
>;

const EditorBlockSequencing = ({
  blockId,
  data,
  isAIPreview,
  isPreview,
  languageVersion,
  updateData,
  ...stateProps
}: BlockProps<BlockSequencing> & BlockSequencingStateProps) => {
  const { editorLanguageActive, editorLanguageReference } = stateProps;

  const { messages, theme } = useReduxState();

  const handleQuestionChange = (html: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            question: html
          }
        }
      }
    });
  };

  const handleOptionChange = (text: string, optionIndex: number) => {
    const optionId = data.options[optionIndex].id;
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            [`option_${optionId}_text`]: text
          }
        }
      }
    });
  };

  const handleOptionDelete = (index: number) => {
    const newOptions = [...data.options];
    const [deletedOption] = newOptions.splice(index, 1);

    const newOptionIds = data.optionIds.filter((id) => id !== deletedOption.id);

    const localeKey = `option_${deletedOption.id}_text` as const;
    delete data.locales[languageVersion].translatable[localeKey];

    updateData({
      ...data,
      optionIds: newOptionIds,
      options: newOptions,
      locales: { ...data.locales }
    });
  };

  const handleOptionSort = (ids: string[]) => {
    const newOptions = ids.map((id) =>
      data.options.find((option) => option.id === id)
    );
    updateData({ ...data, options: newOptions });
  };

  const handleUseRandomOrderChange = (value: boolean) => {
    updateData({ ...data, useRandomOrder: value });
  };

  const insertOption = () => {
    const id = Date.now().toString();

    const newOptionIds = [...data.optionIds, id];

    const newOptions = [
      ...data.options,
      { id, text: `{{ locales.translatable.option_${id}_text }}` as const }
    ];

    updateData({
      ...data,
      optionIds: newOptionIds,
      options: newOptions,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            [`option_${id}_text`]: ''
          }
        }
      }
    });

    setTimeout(() => {
      const optionContentEditable = document
        .getElementById(`sequencing-option-${blockId}-${newOptions.length - 1}`)
        ?.querySelector('input');
      if (optionContentEditable) optionContentEditable.focus();
    });
  };

  const handleFeedbackChange = (html: string, item: string) => {
    updateData({
      ...data,
      locales: {
        ...data.locales,
        [languageVersion]: {
          ...data.locales[languageVersion],
          translatable: {
            ...data.locales[languageVersion].translatable,
            [item]: html
          }
        }
      }
    });
  };

  const handlePointsWeightChange = (value: number) => {
    updateData({ ...data, pointsWeight: value });
  };

  const pointsWeight = data.hasOwnProperty('pointsWeight')
    ? data.pointsWeight
    : defaultBlockData.sequencing.pointsWeight;

  const feedback = [
    {
      id: 'feedbackCorrect',
      tooltip: null,
      icon: 'check-circle' as const,
      color: theme.successStandaloneColor,
      message: messages.editorBlockQuestionFeedbackCorrect
    },
    {
      id: 'feedbackPartiallyCorrect',
      tooltip: null,
      icon: 'question-circle' as const,
      color: theme.warningColor,
      message: messages.editorBlockMultipleChoiceFeedbackPartiallyCorrect
    },
    {
      id: 'feedbackIncorrect',
      tooltip: null,
      icon: 'times-circle' as const,
      color: theme.dangerColor,
      message: messages.editorBlockQuestionFeedbackIncorrect
    }
  ];

  const isEditingLanguageVersion = Boolean(editorLanguageActive);

  return (
    <div className="sequencing">
      <FormGroup maxWidth="100%">
        {!isAIPreview ? (
          <TaskPointWeightSelector
            blockId={blockId}
            blockName={messages.blocksSequencing}
            disabled={isPreview || isEditingLanguageVersion}
            isMandatory={data.isMandatory}
            pointsWeight={pointsWeight}
            onSelection={handlePointsWeightChange}
          />
        ) : null}

        {isAIPreview || isPreview ? (
          <RichTextValue
            html={data.locales[languageVersion].translatable.question}
          />
        ) : (
          <>
            <EditorBlockTranslationReference
              backgroundColor={theme.lightGrey}
              isInnerElement={true}
              referenceHtml={
                data.locales[editorLanguageReference]?.translatable?.question ||
                ''
              }
              translationHtml={
                data.locales[languageVersion].translatable.question
              }
            >
              <TextEditor
                ariaLabel={messages.commonQuestion}
                backgroundColor="inherit"
                content={data.locales[languageVersion].translatable.question}
                onChange={handleQuestionChange}
                padding="0"
                placeholder={messages.commonAddQuestion}
              />
            </EditorBlockTranslationReference>
          </>
        )}
      </FormGroup>

      <div className="correct-options">
        {isAIPreview || isPreview ? (
          <div className="correct-options-container">
            {data.options.map(({ id }, index) => {
              const localizedText =
                data.locales[languageVersion].translatable[
                  `option_${id}_text`
                ] || '';

              return (
                <div className="correct-options-input" key={id}>
                  <EditorBlockSequencingOption
                    index={index}
                    isPreview={isAIPreview || isPreview}
                    option={{ id, text: localizedText }}
                    {...stateProps}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <FormGroup
            label={messages.editorBlockSequencingCorrectOrder}
            maxWidth="100%"
          >
            <SortableList
              items={data.options.map((option) => option.id)}
              itemStyles={{ backgroundColor: 'transparent', padding: 0 }}
              listId={`sequencing-options-${blockId}`}
              moveWithHandle={!isEditingLanguageVersion}
              moveWithHandleIcon="arrows-alt-v"
              onChange={handleOptionSort}
              renderItem={(id, index) => {
                const localizedText =
                  data.locales[languageVersion].translatable[
                    `option_${id}_text`
                  ] || '';

                return (
                  <div className="correct-options-input">
                    <EditorBlockTranslationReference
                      backgroundColor={theme.lightGrey}
                      isInnerElement={true}
                      referenceHtml={
                        data.locales[editorLanguageReference]?.translatable?.[
                          `option_${id}_text`
                        ] || ''
                      }
                      style={{ marginTop: 5, marginBottom: 5 }}
                      translationHtml={localizedText}
                    >
                      <EditorBlockSequencingOption
                        deleteIsDisabled={isEditingLanguageVersion}
                        index={index}
                        isPreview={false}
                        option={{ id, text: localizedText }}
                        onChange={handleOptionChange}
                        onDelete={handleOptionDelete}
                        {...stateProps}
                      />
                    </EditorBlockTranslationReference>
                  </div>
                );
              }}
              syncWithParent={true}
            />

            <div className="add-option-button">
              <Button
                category="light"
                disabled={isEditingLanguageVersion}
                icon="plus"
                onClick={insertOption}
                size="medium"
                text={messages.commonAddOption}
              />
            </div>
          </FormGroup>
        )}
      </div>

      {!isAIPreview ? (
        <EditorBlockSequencingOrder
          blockId={blockId}
          data={data}
          isPreview={isPreview || isEditingLanguageVersion}
          languageVersion={languageVersion}
          onUseRandomOrderChange={handleUseRandomOrderChange}
          updateData={updateData}
        />
      ) : null}

      <div className="feedback">
        {feedback.map((item, i) => (
          <FormGroup
            key={`sequencing-feedback-${blockId}-${i}`}
            label={
              <div className="result-label" data-tooltip={item.tooltip}>
                <div className="result-label-icon">
                  <Icon icon={item.icon} style={{ color: item.color }} />
                </div>
                {item.message}
              </div>
            }
            maxWidth="100%"
          >
            {isAIPreview || isPreview ? (
              <RichTextValue
                html={
                  data.locales[languageVersion].translatable[item.id] || '-'
                }
                style={{
                  backgroundColor: 'white',
                  border: `1px solid ${theme.lightGreyDark}`,
                  borderRadius: theme.inputBorderRadius,
                  color: theme.textColorLight,
                  padding: 10
                }}
              />
            ) : (
              <>
                <EditorBlockTranslationReference
                  backgroundColor={theme.lightGrey}
                  isInnerElement={true}
                  referenceHtml={
                    data.locales[editorLanguageReference]?.translatable?.[
                      item.id
                    ] || ''
                  }
                  style={{ marginTop: 5, marginBottom: 5 }}
                  translationHtml={
                    data.locales[languageVersion].translatable[item.id] || ''
                  }
                >
                  <TextEditor
                    content={
                      data.locales[languageVersion].translatable[item.id] || ''
                    }
                    onChange={(value: string) =>
                      handleFeedbackChange(value, item.id)
                    }
                    placeholder={messages.commonFeedbackPlaceholder}
                    showBorder={true}
                  />
                </EditorBlockTranslationReference>
              </>
            )}
          </FormGroup>
        ))}
      </div>

      {!isAIPreview && !isEditingLanguageVersion ? (
        <EditorBlockMoreSettings
          settingsHaveBeenEdited={!isPreview && Boolean(data?.isMandatory)}
        >
          <EditorBlockIsMandatorySettings
            blockId={blockId}
            data={data}
            disabled={isPreview}
            updateData={updateData}
          />
        </EditorBlockMoreSettings>
      ) : null}

      {!isAIPreview ? (
        <EditorBlockRequireAnswerToProceedInfo data={data} />
      ) : null}

      <style jsx>{`
        .sequencing {
          background-color: ${theme.lightGrey};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .add-option-button {
          margin-top: 10px;
        }
        .correct-options {
          margin-top: 20px;
        }
        .correct-options-input {
          margin: 5px 0;
          width: 100%;
        }
        .feedback {
          margin-top: 20px;
        }
        .result-label {
          align-items: center;
          display: flex;
        }
        .result-label-icon {
          font-size: 1.1rem;
          margin-right: 5px;
          position: relative;
          top: 1px;
        }
      `}</style>
    </div>
  );
};

export default EditorBlockSequencing;
